import React, { useState, useEffect, useCallback } from 'react';
// ⚠️ A MESMA paginação do Compras e da Consulta de chamados, recolorida: o lote
//    de 2026 tem 1.001 etiquetas, e uma tabela de mil linhas trava o celular do
//    gerente antes de ele achar a que procura.
import Paginacao, { POR_PAGINA } from '../../Paginacao';
import useTelaMobile from '../../../utils/useTelaMobile';
import {
    QrCode, Download, Link2, Unlink, Ban, RefreshCw, Search, Plus, AlertTriangle, FlaskConical,
} from 'lucide-react';

// =====================================================================
// Chamados › PATRIMÔNIO — o controle das etiquetas com QR Code
//
// Cada patrimônio de TI e de Manutenção ganha um adesivo com QR. Lido pela
// câmera, ele abre a tela de abrir chamado com o equipamento já identificado.
//
// ⚠️⚠️ A ETIQUETA NÃO É O EQUIPAMENTO — é o que esta tela torna visível. Ela
//    nasce SEM aparelho ("EMITIDA"), porque as 1.000 são impressas antes de se
//    saber onde vão colar. O vínculo é um ato posterior, feito aqui, e é ele que
//    permite trocar o aparelho sem reimprimir o adesivo.
//
// ⚠️ SÓ `Admin` POR ENQUANTO (Julia, 11/09/2026), e isso é decisão, não
//    esquecimento: emitir lote e exportar para a gráfica são atos de
//    implantação. Quando o auditor assumir a colagem em campo, o perfil dele
//    entra no `PERMISSIONS` do hub e no `PERFIS_GESTAO` do servidor — os dois,
//    porque a tela esconde e o servidor recusa.
// =====================================================================

const API = '/api/chamados/etiquetas';
const COR = 'var(--ch-cor)';
const COR_CLARA = 'var(--ch-cor-clara)';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// A cor de cada situação da etiqueta. ⚠️ Estado se lê pela FORMA também, não só
// pelo texto: quem confere um lote de 500 passa o olho, não lê linha a linha.
const CORES_STATUS = {
    EMITIDA: { fundo: '#f1f5f9', texto: '#475569', rotulo: 'Emitida' },
    VINCULADA: { fundo: '#dcfce7', texto: '#166534', rotulo: 'Vinculada' },
    SUBSTITUIDA: { fundo: '#fef3c7', texto: '#92400e', rotulo: 'Substituída' },
    BAIXADA: { fundo: '#fee2e2', texto: '#991b1b', rotulo: 'Baixada' },
};

const DEPARTAMENTOS = [
    { id: 'TI', rotulo: 'TI' },
    { id: 'MAN', rotulo: 'Manutenção' },
];

/** Data legível, sem inventar hora que não existe. */
const dataBr = (v) => (v ? new Date(v).toLocaleDateString('pt-BR') : '—');

const ChamadosPatrimonio = () => {
    // ⚠️ Esta tela É usada no celular, apesar de parecer de escritório: quem
    //    cola a etiqueta está de pé ao lado do aparelho, com o adesivo na mão.
    const { ehMobile } = useTelaMobile();
    const [etiquetas, setEtiquetas] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [aviso, setAviso] = useState('');

    const [fDepartamento, setFDepartamento] = useState('');
    const [fStatus, setFStatus] = useState('');
    const [fBusca, setFBusca] = useState('');
    // ⚠️ Volta para a página 1 a cada FILTRO (ver os `onChange` abaixo), nunca a
    //    cada recarga: `carregar()` roda também depois de vincular e baixar, e
    //    zerar nessas horas jogaria a pessoa para o topo do lote no meio do
    //    trabalho — o mesmo cuidado que a Consulta já documenta.
    const [pagina, setPagina] = useState(1);

    // O formulário de emissão de lote.
    const [emitindo, setEmitindo] = useState(false);
    const [novoDep, setNovoDep] = useState('TI');
    const [novoInicio, setNovoInicio] = useState(1);
    const [novaQtd, setNovaQtd] = useState(10);

    // O vínculo em andamento: qual etiqueta e qual equipamento.
    const [vinculando, setVinculando] = useState(null);   // a etiqueta inteira
    const [ativos, setAtivos] = useState([]);
    const [buscaAtivo, setBuscaAtivo] = useState('');
    const [carregandoAtivos, setCarregandoAtivos] = useState(false);

    // ---------------------------------------------------------------
    // A PÁGINA À VISTA
    //
    // ⚠️ A FILTRAGEM É DO SERVIDOR (`?departamento=&status=&busca=`), e é ele
    //    quem devolve a lista já recortada — então aqui só se fatia. Paginar
    //    antes de filtrar mostraria "página 1 de 21" com o filtro aplicado.
    // ⚠️ `paginaValida` em vez de confiar no estado: apagar/baixar a última
    //    etiqueta da página 3 deixaria a pessoa numa página que não existe mais,
    //    olhando uma tabela vazia e achando que perdeu o lote.
    // ---------------------------------------------------------------
    const totalPaginas = Math.max(1, Math.ceil(etiquetas.length / POR_PAGINA));
    const paginaValida = Math.min(pagina, totalPaginas);
    const daPagina = etiquetas.slice((paginaValida - 1) * POR_PAGINA, paginaValida * POR_PAGINA);

    const carregar = useCallback(async () => {
        setCarregando(true); setErro('');
        try {
            const qs = new URLSearchParams();
            if (fDepartamento) qs.set('departamento', fDepartamento);
            if (fStatus) qs.set('status', fStatus);
            if (fBusca.trim()) qs.set('busca', fBusca.trim());
            const r = await fetch(`${API}?${qs}`, { headers: auth() });
            if (r.status === 403) throw new Error('Seu perfil não tem acesso ao controle de etiquetas.');
            if (!r.ok) throw new Error(`A lista não carregou (HTTP ${r.status}).`);
            setEtiquetas(await r.json());
        } catch (e) {
            // ⚠️ "não carregou" e "está vazio" são estados DIFERENTES: zerar a
            //    lista no catch faria falha de rede parecer lote inexistente.
            setErro(e.message);
        } finally {
            setCarregando(false);
        }
    }, [fDepartamento, fStatus, fBusca]);

    useEffect(() => { carregar(); }, [carregar]);

    // ---------------------------------------------------------------
    // Emitir lote
    // ---------------------------------------------------------------
    const emitir = async () => {
        setEmitindo(true); setErro(''); setAviso('');
        try {
            const r = await fetch(`${API}/emitir`, {
                method: 'POST',
                headers: { ...auth(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    departamento: novoDep, inicio: Number(novoInicio), quantidade: Number(novaQtd),
                }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message || 'Não foi possível emitir o lote.');
            setAviso(
                `${d.criadas} etiqueta(s) emitida(s) — de ${d.primeiro} a ${d.ultimo}.`
                + (d.jaExistiam ? ` ${d.jaExistiam} já existiam e ficaram como estavam.` : '')
            );
            carregar();
        } catch (e) {
            setErro(e.message);
        } finally {
            setEmitindo(false);
        }
    };

    // ---------------------------------------------------------------
    // Vincular — a etiqueta ganha o aparelho em que foi colada
    // ---------------------------------------------------------------
    // ⚠️ A lista de equipamentos sai do catálogo do DEPARTAMENTO da etiqueta:
    //    TI vem do parque de chamados, Manutenção vem do cadastro da
    //    Certificação. São dois módulos — é o departamento que resolve qual.
    const abrirVinculo = async (etiqueta) => {
        setVinculando(etiqueta);
        setBuscaAtivo(''); setAtivos([]); setCarregandoAtivos(true); setErro('');
        try {
            const url = etiqueta.DEPARTAMENTO === 'TI'
                ? '/api/chamados/ativos?ativos=1'
                : '/api/manutencao/ativos?ativos=1';
            const r = await fetch(url, { headers: auth() });
            if (!r.ok) throw new Error(`O catálogo de equipamentos não carregou (HTTP ${r.status}).`);
            setAtivos(await r.json());
        } catch (e) {
            setErro(e.message);
        } finally {
            setCarregandoAtivos(false);
        }
    };

    const vincular = async (codigoAtivo) => {
        setErro(''); setAviso('');
        try {
            const r = await fetch(`${API}/${vinculando.CODIGO}/vincular`, {
                method: 'POST',
                headers: { ...auth(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_ativo: codigoAtivo }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message || 'Não foi possível vincular.');
            setAviso(d.message);
            setVinculando(null);
            carregar();
        } catch (e) {
            setErro(e.message);
        }
    };

    const desvincular = async (etiqueta) => {
        const motivo = window.prompt(
            `Desvincular a etiqueta ${etiqueta.CODIGO} do equipamento.\n\n`
            + 'Por quê? (o adesivo continua colado e pode ser reaproveitado)'
        );
        if (motivo === null) return;
        setErro(''); setAviso('');
        try {
            const r = await fetch(`${API}/${etiqueta.CODIGO}/desvincular`, {
                method: 'POST',
                headers: { ...auth(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ motivo }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message || 'Não foi possível desvincular.');
            setAviso(d.message);
            carregar();
        } catch (e) { setErro(e.message); }
    };

    const baixar = async (etiqueta) => {
        // ⚠️ Etiqueta não se apaga: se baixa com motivo. O adesivo continua
        //    colado num aparelho do mundo real — sem o motivo, ninguém saberia
        //    por que o QR parou de levar a lugar nenhum.
        const motivo = window.prompt(
            `Baixar a etiqueta ${etiqueta.CODIGO} (tirar de uso).\n\nPor quê?`
        );
        if (motivo === null) return;
        if (!motivo.trim()) { setErro('A baixa precisa de um motivo.'); return; }
        setErro(''); setAviso('');
        try {
            const r = await fetch(`${API}/${etiqueta.CODIGO}/baixar`, {
                method: 'POST',
                headers: { ...auth(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ motivo }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message || 'Não foi possível baixar.');
            setAviso(d.message);
            carregar();
        } catch (e) { setErro(e.message); }
    };

    // ---------------------------------------------------------------
    // Baixar arquivo (SVG de uma, ZIP do lote)
    // ---------------------------------------------------------------
    // ⚠️ `fetch` + blob em vez de um `<a href>` simples: a rota exige o token no
    //    cabeçalho, e link direto do navegador não manda cabeçalho nenhum — cairia
    //    num 401 silencioso, com o navegador baixando a página de erro.
    const baixarArquivo = async (url, nomeArquivo) => {
        setErro('');
        try {
            const r = await fetch(url, { headers: auth() });
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                throw new Error(d.message || `O arquivo não veio (HTTP ${r.status}).`);
            }
            const blob = await r.blob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = nomeArquivo;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
        } catch (e) { setErro(e.message); }
    };

    // ---------------------------------------------------------------
    // Estilos
    // ---------------------------------------------------------------
    const caixa = {
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
        padding: '18px 20px', marginBottom: '18px',
    };
    const rotulo = {
        display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '.06em',
        textTransform: 'uppercase', color: '#64748b', marginBottom: '5px',
    };
    const campo = {
        padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: '8px',
        fontSize: '14px', width: '100%', boxSizing: 'border-box',
    };
    const botao = (primario = false) => ({
        display: 'inline-flex', alignItems: 'center', gap: '7px',
        padding: '9px 14px', borderRadius: '8px', fontSize: '13.5px', fontWeight: 600,
        cursor: 'pointer', border: primario ? 'none' : '1px solid #cbd5e1',
        background: primario ? COR : '#fff', color: primario ? '#fff' : '#334155',
    });
    // ⚠️ `tabular-nums` em todo código de etiqueta: é uma coluna de números que
    //    se confere passando o olho, e fonte proporcional desalinha os dígitos.
    const codigoCss = {
        fontFamily: 'ui-monospace, Consolas, monospace', fontVariantNumeric: 'tabular-nums',
        fontWeight: 700, fontSize: '13.5px', letterSpacing: '.02em',
    };

    /** A faixa de teste, marcada para ninguém colar um adesivo de demonstração
     *  num aparelho de verdade. */
    const ehEtiquetaDeTeste = (et) => Number(String(et.CODIGO).split('-')[1]) >= 99000;

    const seloTeste = (
        <span title="Faixa reservada para teste" style={{
            marginLeft: '7px', display: 'inline-flex', alignItems: 'center',
            gap: '3px', fontSize: '10.5px', fontWeight: 700, color: '#92400e',
            background: '#fef3c7', padding: '1px 6px', borderRadius: '4px',
        }}>
            <FlaskConical size={11} /> TESTE
        </span>
    );

    /**
     * Os botões da etiqueta — os MESMOS na tabela e no cartão.
     *
     * ⚠️⚠️ EXTRAÍDOS PARA NÃO DIVERGIREM. São quatro botões cuja presença depende
     *    do `STATUS`, e duplicá-los faria o celular ganhar (ou perder) uma ação
     *    numa mudança futura sem ninguém notar — a etiqueta é patrimônio, e
     *    "desvincular some no celular" é o tipo de bug que se descobre na loja.
     * ⚠️ O SVG fica no celular de propósito: quem está ao lado do aparelho pode
     *    precisar reimprimir a etiqueta rasgada, e tirar a ação daqui obrigaria
     *    a voltar ao computador para uma coisa de dez segundos.
     */
    const acoesDaEtiqueta = (et) => (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <button type="button" title="Baixar o QR Code em SVG"
                style={{ ...botao(), padding: '6px 9px' }}
                onClick={() => baixarArquivo(
                    `${API}/${et.CODIGO}/qrcode.svg`, `${et.CODIGO}.svg`
                )}>
                <QrCode size={14} /> SVG
            </button>
            {et.STATUS === 'EMITIDA' && (
                <button type="button" style={{ ...botao(), padding: '6px 9px' }}
                    onClick={() => abrirVinculo(et)}>
                    <Link2 size={14} /> Vincular
                </button>
            )}
            {et.STATUS === 'VINCULADA' && (
                <button type="button" style={{ ...botao(), padding: '6px 9px' }}
                    onClick={() => desvincular(et)}>
                    <Unlink size={14} /> Desvincular
                </button>
            )}
            {et.STATUS !== 'BAIXADA' && (
                <button type="button" title="Tirar de uso"
                    style={{ ...botao(), padding: '6px 9px', color: '#991b1b' }}
                    onClick={() => baixar(et)}>
                    <Ban size={14} />
                </button>
            )}
        </div>
    );

    /**
     * A etiqueta em CARTÃO (celular).
     *
     * ⚠️⚠️ NÃO É A TABELA ENCOLHIDA. São 6 colunas e 760px de largura mínima; num
     *    aparelho de 360px isso vira arrasto lateral, e a coluna *Ações* — que é
     *    o motivo de a tela existir — fica do lado de fora.
     *
     * ⚠️ QUEM MANDA É O CÓDIGO IMPRESSO, e por um motivo concreto: é a única
     *    coisa que a pessoa consegue LER no adesivo que está com ela na mão. Ela
     *    não chega aqui procurando uma loja nem um status — chega procurando
     *    `M-0001`. Por isso ele vem primeiro e em monoespaçada, e a situação vem
     *    logo ao lado, que é a segunda pergunta ("esta já foi colada?").
     * ⚠️ Vínculo (quem colou e quando) desce para o rodapé: é a procedência, e
     *    quem precisa dela já parou para ler o cartão.
     */
    const cartaoDaEtiqueta = (et) => {
        const cor = CORES_STATUS[et.STATUS] || CORES_STATUS.EMITIDA;
        return (
            <div key={et.CODIGO} style={{
                border: '1px solid #e2e8f0', borderLeft: `4px solid ${cor.texto}`,
                borderRadius: '10px', background: '#fff', padding: '12px', marginBottom: '10px',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <span style={codigoCss}>{et.CODIGO_IMPRESSO || et.CODIGO}</span>
                    {ehEtiquetaDeTeste(et) && seloTeste}
                    <span style={{
                        marginLeft: 'auto', background: cor.fundo, color: cor.texto,
                        padding: '3px 9px', borderRadius: '20px', fontSize: '11.5px', fontWeight: 700,
                    }}>{cor.rotulo}</span>
                </div>

                {et.ID_ATIVO ? (
                    <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#334155', lineHeight: 1.4 }}>
                        <span style={codigoCss}>{et.ID_ATIVO}</span>
                        <br />
                        <span style={{ color: '#64748b' }}>{et.ATIVO_DESCRICAO || 'sem descrição'}</span>
                    </p>
                ) : (
                    <p style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#94a3b8' }}>
                        — ainda não colada
                    </p>
                )}

                <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: '2px 10px',
                    fontSize: '12px', color: '#64748b', marginBottom: '10px',
                }}>
                    <span style={{ fontWeight: 700, color: '#334155' }}>{et.ATIVO_LOJA || et.LOJA || '—'}</span>
                    {et.VINCULADO_POR_NOME && (
                        <span>{et.VINCULADO_POR_NOME} · {dataBr(et.VINCULADO_EM)}</span>
                    )}
                </div>

                {acoesDaEtiqueta(et)}
            </div>
        );
    };

    const ativosFiltrados = (() => {
        const q = buscaAtivo.trim().toLowerCase();
        if (!q) return ativos.slice(0, 40);
        return ativos.filter((a) => [a.CODIGO, a.DESCRICAO, a.DENOMINACAO, a.MODELO, a.LOJA, a.NUMERO_ATIVO, a.EQUIPAMENTO]
            .filter(Boolean).join(' ').toLowerCase().includes(q)).slice(0, 40);
    })();

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <QrCode size={22} color={COR} />
                <h2 style={{ margin: 0, fontSize: '19px', fontWeight: 700, color: '#1e293b' }}>
                    Patrimônio — etiquetas com QR Code
                </h2>
            </div>
            <p style={{ margin: '0 0 18px', fontSize: '14px', color: '#64748b', maxWidth: '70ch' }}>
                Cada adesivo leva um QR que abre o chamado com o equipamento já identificado.
                A etiqueta nasce sem aparelho — o vínculo é feito aqui, na hora de colar, e pode
                ser trocado depois sem reimprimir nada.
            </p>

            {erro && (
                <div style={{
                    ...caixa, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b',
                    display: 'flex', gap: '9px', alignItems: 'flex-start', fontSize: '14px',
                }}>
                    <AlertTriangle size={17} style={{ flexShrink: 0, marginTop: '1px' }} />
                    <span>{erro}</span>
                </div>
            )}
            {aviso && (
                <div style={{
                    ...caixa, background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534', fontSize: '14px',
                }}>
                    {aviso}
                </div>
            )}

            {/* ---------- Emitir lote ---------- */}
            <div style={caixa}>
                <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>
                    Emitir lote
                </h3>
                <p style={{ margin: '0 0 14px', fontSize: '13px', color: '#64748b' }}>
                    Cria os códigos no portal. Emitir de novo uma faixa que já existe não mexe
                    nas etiquetas já coladas.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
                    <div style={{ minWidth: '150px' }}>
                        <label style={rotulo} htmlFor="et-dep">Departamento</label>
                        <select id="et-dep" style={campo} value={novoDep} onChange={(e) => setNovoDep(e.target.value)}>
                            {DEPARTAMENTOS.map((d) => <option key={d.id} value={d.id}>{d.rotulo}</option>)}
                        </select>
                    </div>
                    <div style={{ width: '120px' }}>
                        <label style={rotulo} htmlFor="et-ini">Começa em</label>
                        <input id="et-ini" style={campo} type="number" min="1" max="99999"
                            value={novoInicio} onChange={(e) => setNovoInicio(e.target.value)} />
                    </div>
                    <div style={{ width: '120px' }}>
                        <label style={rotulo} htmlFor="et-qtd">Quantas</label>
                        <input id="et-qtd" style={campo} type="number" min="1" max="1000"
                            value={novaQtd} onChange={(e) => setNovaQtd(e.target.value)} />
                    </div>
                    <button type="button" style={botao(true)} onClick={emitir} disabled={emitindo}>
                        <Plus size={16} /> {emitindo ? 'Emitindo…' : 'Emitir'}
                    </button>
                    <button type="button" style={botao()}
                        onClick={() => baixarArquivo(
                            `${API}/exportar.zip?departamento=${novoDep}`,
                            `etiquetas-${novoDep}.zip`
                        )}>
                        <Download size={16} /> Exportar pacote da gráfica ({novoDep})
                    </button>
                </div>
                <p style={{ margin: '12px 0 0', fontSize: '12.5px', color: '#94a3b8' }}>
                    A faixa <strong>99000–99999</strong> é reservada para teste — a etiqueta{' '}
                    <span style={codigoCss}>TI-99999</span> é a de demonstração e fica fora da
                    produção de propósito. O pacote traz os QR Codes em SVG, a planilha de
                    conferência e as exigências técnicas do adesivo.
                    {' '}<strong>O adesivo é impresso na forma curta</strong> (
                    <span style={codigoCss}>M-0001</span>, <span style={codigoCss}>TI-0001</span>) e é
                    assim que os códigos aparecem nesta tela; dentro do QR vai a forma longa
                    (<span style={codigoCss}>MAN-00001</span>), que é a que o portal guarda. Digitar
                    qualquer uma das duas na busca funciona.
                </p>
            </div>

            {/* ---------- Filtros ---------- */}
            <div style={{ ...caixa, display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
                <div style={{ minWidth: '150px' }}>
                    <label style={rotulo} htmlFor="f-dep">Departamento</label>
                    <select id="f-dep" style={campo} value={fDepartamento} onChange={(e) => { setFDepartamento(e.target.value); setPagina(1); }}>
                        <option value="">Todos</option>
                        {DEPARTAMENTOS.map((d) => <option key={d.id} value={d.id}>{d.rotulo}</option>)}
                    </select>
                </div>
                <div style={{ minWidth: '150px' }}>
                    <label style={rotulo} htmlFor="f-status">Situação</label>
                    <select id="f-status" style={campo} value={fStatus} onChange={(e) => { setFStatus(e.target.value); setPagina(1); }}>
                        <option value="">Todas</option>
                        {Object.entries(CORES_STATUS).map(([k, v]) => <option key={k} value={k}>{v.rotulo}</option>)}
                    </select>
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={rotulo} htmlFor="f-busca">Buscar</label>
                    <div style={{ position: 'relative' }}>
                        <Search size={15} color="#94a3b8"
                            style={{ position: 'absolute', left: '10px', top: '11px' }} />
                        <input id="f-busca" style={{ ...campo, paddingLeft: '32px' }}
                            placeholder="Código da etiqueta, equipamento ou loja"
                            value={fBusca} onChange={(e) => { setFBusca(e.target.value); setPagina(1); }} />
                    </div>
                </div>
                <button type="button" style={botao()} onClick={carregar}>
                    <RefreshCw size={15} /> Atualizar
                </button>
            </div>

            {/* ---------- Lista ---------- */}
            {/* ⚠️ No celular a lista é de CARTÕES, e aí `overflowX` e o `padding:0`
                (que existem para a tabela encostar nas bordas e rolar de lado)
                só atrapalham — o cartão precisa do respiro interno. */}
            <div style={ehMobile ? caixa : { ...caixa, padding: 0, overflowX: 'auto' }}>
                {carregando ? (
                    <p style={{ padding: '28px', textAlign: 'center', color: '#64748b' }}>Carregando…</p>
                ) : etiquetas.length === 0 ? (
                    <p style={{ padding: '28px', textAlign: 'center', color: '#64748b' }}>
                        Nenhuma etiqueta com esses filtros. Emita um lote acima para começar.
                    </p>
                ) : ehMobile ? (
                    <div>{daPagina.map(cartaoDaEtiqueta)}</div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13.5px', minWidth: '760px' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc' }}>
                                {['Etiqueta', 'Situação', 'Equipamento', 'Loja', 'Vínculo', 'Ações'].map((h) => (
                                    <th key={h} style={{
                                        textAlign: 'left', padding: '11px 14px', fontSize: '11px',
                                        fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                                        color: '#64748b', borderBottom: '1px solid #e2e8f0',
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {daPagina.map((et) => {
                                const cor = CORES_STATUS[et.STATUS] || CORES_STATUS.EMITIDA;
                                const ehTeste = ehEtiquetaDeTeste(et);
                                return (
                                    <tr key={et.CODIGO} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '11px 14px' }}>
                                            {/* ⚠️⚠️ MOSTRA O CÓDIGO COMO ELE ESTÁ IMPRESSO no adesivo
                                                (`M-0001`), não como está no banco (`MAN-00001`).
                                                A arte do lote de 2026 encurtou o prefixo e o número,
                                                e a tela precisa dizer a MESMA palavra que a pessoa
                                                está lendo no aparelho — senão ela entende que achou
                                                outra etiqueta. O canônico segue no `CODIGO`, que é o
                                                que vai nas ações e dentro do QR. */}
                                            <span style={codigoCss}>{et.CODIGO_IMPRESSO || et.CODIGO}</span>
                                            {ehTeste && seloTeste}
                                        </td>
                                        <td style={{ padding: '11px 14px' }}>
                                            <span style={{
                                                background: cor.fundo, color: cor.texto, padding: '3px 9px',
                                                borderRadius: '20px', fontSize: '11.5px', fontWeight: 700,
                                            }}>{cor.rotulo}</span>
                                        </td>
                                        <td style={{ padding: '11px 14px', color: '#334155' }}>
                                            {et.ID_ATIVO ? (
                                                <>
                                                    <span style={codigoCss}>{et.ID_ATIVO}</span>
                                                    <br />
                                                    <span style={{ fontSize: '12.5px', color: '#64748b' }}>
                                                        {et.ATIVO_DESCRICAO || 'sem descrição'}
                                                    </span>
                                                </>
                                            ) : <span style={{ color: '#94a3b8' }}>— ainda não colada</span>}
                                        </td>
                                        <td style={{ padding: '11px 14px', color: '#334155' }}>
                                            {et.ATIVO_LOJA || et.LOJA || '—'}
                                        </td>
                                        <td style={{ padding: '11px 14px', fontSize: '12.5px', color: '#64748b' }}>
                                            {et.VINCULADO_POR_NOME
                                                ? <>{et.VINCULADO_POR_NOME}<br />{dataBr(et.VINCULADO_EM)}</>
                                                : '—'}
                                        </td>
                                        <td style={{ padding: '11px 14px' }}>
                                            {acoesDaEtiqueta(et)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                {/* ⚠️ FORA do `{carregando ? … : …}` e depois da tabela: a
                    paginação some junto com a tabela quando não há etiqueta
                    nenhuma, mas continua visível durante o recarregamento — o
                    contador saltando de lugar a cada ação é o que faz a tela
                    "piscar". */}
                {etiquetas.length > 0 && (
                    <Paginacao
                        total={etiquetas.length}
                        pagina={paginaValida}
                        onMudar={setPagina}
                        cor={COR}
                        fundo={COR_CLARA}
                    />
                )}
            </div>

            {/* ---------- Modal do vínculo ---------- */}
            {vinculando && (
                <div
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
                    }}
                    onClick={() => setVinculando(null)}
                >
                    <div
                        style={{
                            background: '#fff', borderRadius: '14px', padding: '22px',
                            width: '100%', maxWidth: '620px', maxHeight: '82vh', overflowY: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>
                            Vincular a etiqueta <span style={codigoCss}>{vinculando.CODIGO_IMPRESSO || vinculando.CODIGO}</span>
                        </h3>
                        <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#64748b' }}>
                            Escolha o equipamento em que este adesivo foi colado
                            {vinculando.DEPARTAMENTO === 'TI'
                                ? ' — parque de TI.'
                                : ' — cadastro de equipamentos da Manutenção.'}
                        </p>

                        <input
                            id="et-busca-ativo"
                            style={{ ...campo, marginBottom: '14px' }}
                            placeholder="Buscar por equipamento, loja ou patrimônio"
                            value={buscaAtivo}
                            onChange={(e) => setBuscaAtivo(e.target.value)}
                            autoFocus
                        />

                        {carregandoAtivos ? (
                            <p style={{ color: '#64748b', fontSize: '14px' }}>Carregando o catálogo…</p>
                        ) : ativosFiltrados.length === 0 ? (
                            <p style={{ color: '#64748b', fontSize: '14px' }}>
                                Nenhum equipamento encontrado com esse texto.
                            </p>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {ativosFiltrados.map((a) => (
                                    <button
                                        key={a.CODIGO}
                                        type="button"
                                        onClick={() => vincular(a.CODIGO)}
                                        style={{
                                            textAlign: 'left', padding: '10px 12px', borderRadius: '8px',
                                            border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = COR_CLARA; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                                    >
                                        <span style={codigoCss}>{a.CODIGO}</span>
                                        <span style={{ marginLeft: '9px', fontSize: '13.5px', color: '#334155' }}>
                                            {a.DESCRICAO || a.DENOMINACAO || a.EQUIPAMENTO || 'sem descrição'}
                                        </span>
                                        <br />
                                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                                            {[a.LOJA, a.FAMILIA, a.NUMERO_ATIVO && `patrimônio ${a.NUMERO_ATIVO}`]
                                                .filter(Boolean).join(' · ')}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}

                        <button type="button" style={{ ...botao(), marginTop: '16px' }}
                            onClick={() => setVinculando(null)}>
                            Cancelar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChamadosPatrimonio;
